<template>
  <nav class="bg-white shadow-sm border-b border-gray-200 w-full">
    <div class="px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <div class="flex">
          <div class="flex-shrink-0 flex items-center">
            <RouterLink to="/tasks" class="flex items-center h-8">
              <img src="/app-logo.svg" alt="TT Tasks" class="h-8 w-auto" />
            </RouterLink>
          </div>
          <div class="hidden sm:ml-6 sm:flex sm:space-x-8">
            <RouterLink
              to="/tasks"
              class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors"
              :class="isActive('/tasks') 
                ? 'border-blue-500 text-gray-900' 
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'"
            >
              Tasks
            </RouterLink>
            <RouterLink
              to="/projects"
              class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors"
              :class="isActive('/projects') 
                ? 'border-blue-500 text-gray-900' 
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'"
            >
              Projects
            </RouterLink>
            <RouterLink
              to="/schedule"
              class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors"
              :class="isActive('/schedule') 
                ? 'border-blue-500 text-gray-900' 
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'"
            >
              Schedule
            </RouterLink>
            <RouterLink
              to="/deadlines"
              class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors"
              :class="isActive('/deadlines') 
                ? 'border-blue-500 text-gray-900' 
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'"
            >
              Deadlines
            </RouterLink>
            
            <!-- Settings Dropdown -->
            <Menu as="div" class="relative inline-flex items-center">
              <MenuButton
                class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors"
                :class="isSettingsActive 
                  ? 'border-blue-500 text-gray-900' 
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'"
              >
                Settings
                <ChevronDownIcon class="ml-1 h-4 w-4" />
              </MenuButton>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems class="absolute left-0 top-full mt-2 w-48 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                  <div class="py-1">
                    <MenuItem v-slot="{ active }">
                      <RouterLink
                        to="/time-off"
                        :class="[
                          active ? 'bg-gray-100' : '',
                          'block px-4 py-2 text-sm text-gray-700'
                        ]"
                      >
                        Time Off
                      </RouterLink>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                      <RouterLink
                        to="/holidays"
                        :class="[
                          active ? 'bg-gray-100' : '',
                          'block px-4 py-2 text-sm text-gray-700'
                        ]"
                      >
                        Holidays
                      </RouterLink>
                    </MenuItem>
                    <MenuItem v-slot="{ active }">
                      <RouterLink
                        to="/tags"
                        :class="[
                          active ? 'bg-gray-100' : '',
                          'block px-4 py-2 text-sm text-gray-700'
                        ]"
                      >
                        Project Tags
                      </RouterLink>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>

        <!-- User Menu -->
        <div class="flex items-center space-x-4">
          <div v-if="currentUser" class="flex items-center space-x-3">
            <img
              v-if="currentUser.picture"
              :src="currentUser.picture"
              :alt="currentUser.name"
              class="h-8 w-8 rounded-full"
            />
            <div class="hidden sm:block">
              <p class="text-sm font-medium text-gray-900">{{ currentUser.name }}</p>
              <p class="text-xs text-gray-500">{{ currentUser.email }}</p>
            </div>
          </div>
          <button
            @click="handleLogout"
            class="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router'
import { computed, ref } from 'vue'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { getCurrentUser, signOut } from '@/composables/auth'

const route = useRoute()
const router = useRouter()
const currentUser = computed(() => getCurrentUser())

const isActive = (path) => {
  return computed(() => route.path.startsWith(path)).value
}

const isSettingsActive = computed(() => {
  return route.path.startsWith('/time-off') || 
         route.path.startsWith('/holidays') || 
         route.path.startsWith('/tags')
})

function handleLogout() {
  signOut()
  router.push('/login')
}
</script>
